/**
 * Serialize a structured-data object for safe injection into an inline
 * <script type="application/ld+json"> via dangerouslySetInnerHTML.
 *
 * JSON.stringify alone is NOT safe here: it does not escape "<", so a value
 * containing "</script>" (or "<!--") closes the <script> element early and lets
 * arbitrary markup run on this origin. On the white-label storefronts some
 * structured-data values come from partner-controlled input (e.g. the partner's
 * display name, which a partner can self-edit), so a raw JSON.stringify is a
 * stored-XSS vector on the sukuto.com origin.
 *
 * Escaping "<", ">" and "&" to their \uXXXX JSON escapes keeps the output valid
 * JSON-LD — Google parses "<" identically to "<" — while making it
 * impossible to break out of the <script> element. (A ld+json block is a data
 * island the browser never executes as JS, so escaping the element-closing
 * characters is sufficient; the U+2028/U+2029 concern only applies to executable
 * inline scripts.) As a bonus this also stops a legitimate name containing "&"
 * or "<" — e.g. "Tom & Jerry Rentals" — from producing invalid markup.
 *
 * @param {unknown} data JSON-serializable structured-data object
 * @returns {string} HTML-safe JSON string for dangerouslySetInnerHTML
 */
export function safeJsonLd(data) {
  return JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026");
}

/**
 * Trailing slash for a URL used inside structured data.
 *
 * next.config.mjs sets trailingSlash: true, so Next normalises the canonical
 * and og:url tags at render — but a URL written straight into a JSON-LD block
 * keeps whatever the database holds. Several seo.canonicalUrl values have no
 * trailing slash, so Service.url pointed at a 308 and disagreed with the
 * canonical on the same page.
 */
export function withTrailingSlash(url) {
  if (!url || typeof url !== "string") return url;
  const [base, hash = ""] = url.split("#");
  const [path, query = ""] = base.split("?");
  if (!path || path.endsWith("/")) return url;
  return (
    `${path}/` + (query ? `?${query}` : "") + (hash ? `#${hash}` : "")
  );
}

export default safeJsonLd;
